"use client";

import { useRef, useState } from "react";
import { m } from "framer-motion";
import Image from "next/image";
import { Play, Clock, ShieldCheck } from "lucide-react";
import { canOptimizeImage } from "@/lib/images";
import { INTRO_VIDEO_TEXTS, type IntroVideoConfig } from "@/lib/intro-video";

/**
 * Kurzvideo auf der Startseite.
 *
 * Bekommt die Quelle vom Server (siehe intro-video-section.tsx):
 *   • Datei (MP4/WebM, z. B. Supabase-Speicher): natives <video>, lädt erst
 *     Metadaten, spielt nach Klick.
 *   • YouTube: erst nach Klick wird der Player von youtube-nocookie.com
 *     geladen – vorher keine Verbindung zu Google (Datenschutz).
 *
 * Kein Autoplay, kein Ton ohne Klick. Ist kein Video hinterlegt, rendert
 * der Server den Block gar nicht.
 */
export type IntroVideoTexts = { kicker: string; title: string; description: string };
export type IntroVideoBullet = { label: string; sub: string };

const DEFAULT_BULLETS: IntroVideoBullet[] = [
  { label: "Persönliche Vorstellung", sub: "Wer wir sind & wofür wir stehen." },
  { label: "Unser Ansatz", sub: "Wie Design, Marketing & Dev zusammenspielen." },
  { label: "Was Sie bekommen", sub: "Konkrete Ergebnisse statt Buzzwords." },
];

export function IntroVideo({
  video,
  texts = INTRO_VIDEO_TEXTS,
  bullets = DEFAULT_BULLETS,
  headingId,
}: {
  video: IntroVideoConfig;
  /** Überschriften des Blocks – Standard: Startseite; die Motion-Seite übergibt eigene. */
  texts?: IntroVideoTexts;
  /** Drei Stichpunkte unter dem Video. */
  bullets?: IntroVideoBullet[];
  /** id der H2 (für aria-labelledby der umgebenden Sektion). */
  headingId?: string;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [playing, setPlaying] = useState(false);

  const start = () => {
    setPlaying(true);
    if (video.kind === "file") {
      // Erst nach dem Render mit controls starten.
      requestAnimationFrame(() => videoRef.current?.play().catch(() => setPlaying(false)));
    }
  };

  const poster = video.poster;

  return (
    <section className="py-16 lg:py-20" style={{ backgroundColor: "var(--bg)" }} aria-labelledby={headingId}>
      <div className="mx-auto max-w-7xl px-4 lg:px-8">
        <m.div
          className="mx-auto max-w-2xl text-center"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.4 }}
        >
          <p className="text-sm font-semibold text-brand-500">{texts.kicker}</p>
          <h2
            id={headingId}
            className="mt-3 text-3xl font-semibold tracking-tight sm:text-4xl"
            style={{ color: "var(--text)", letterSpacing: "-0.02em" }}
          >
            {texts.title}
          </h2>
          <p className="mt-4 text-lg leading-relaxed" style={{ color: "var(--text-tertiary)" }}>
            {texts.description}
          </p>
        </m.div>

        <m.div
          className="relative mx-auto mt-12 aspect-video w-full max-w-5xl overflow-hidden rounded-2xl border"
          style={{
            borderColor: "var(--border)",
            backgroundColor: "#0B1220",
            boxShadow: "var(--card-shadow-xl)",
          }}
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.5 }}
        >
          {/* Vorschau: eigenes Bild oder ruhiger Verlauf. */}
          {!playing && (
            <div className="absolute inset-0">
              {poster ? (
                canOptimizeImage(poster) ? (
                  // Vorschaubild in passender Breite (AVIF/WebP) statt 1920er PNG.
                  <Image src={poster} alt="" fill sizes="(min-width: 1024px) 1024px, 100vw" className="object-cover" />
                ) : (
                  // eslint-disable-next-line @next/next/no-img-element
                  <img src={poster} alt="" className="h-full w-full object-cover" loading="lazy" decoding="async" />
                )
              ) : (
                <div
                  className="h-full w-full"
                  style={{
                    background:
                      "radial-gradient(circle at 30% 30%, var(--color-brand-500) 0%, transparent 55%), radial-gradient(circle at 70% 70%, #0E7490 0%, transparent 55%), #0B1220",
                    opacity: 0.85,
                  }}
                />
              )}
            </div>
          )}

          {video.kind === "file" && (playing || !poster) && (
            <video
              ref={videoRef}
              className={`relative h-full w-full object-cover ${playing ? "" : "opacity-0"}`}
              poster={poster ?? undefined}
              controls={playing}
              playsInline
              preload="metadata"
              onEnded={() => setPlaying(false)}
              crossOrigin={video.captions ? "anonymous" : undefined}
            >
              <source src={video.src} />
              {video.captions && <track kind="captions" srcLang="de" label="Deutsch" src={video.captions} default />}
            </video>
          )}

          {video.kind === "youtube" && playing && (
            <iframe
              className="absolute inset-0 h-full w-full"
              src={`https://www.youtube-nocookie.com/embed/${video.videoId}?autoplay=1&rel=0&modestbranding=1`}
              title={`Video: ${texts.title}`}
              allow="autoplay; encrypted-media; picture-in-picture"
              allowFullScreen
              referrerPolicy="strict-origin-when-cross-origin"
            />
          )}

          {!playing && (
            <button
              type="button"
              onClick={start}
              aria-label="Intro-Video abspielen"
              className="absolute inset-0 z-10 flex items-center justify-center transition-colors hover:bg-black/10"
            >
              <span className="flex h-20 w-20 items-center justify-center rounded-full bg-white/95 shadow-2xl transition-transform hover:scale-105 sm:h-24 sm:w-24">
                <Play className="ml-1 h-8 w-8 fill-current text-brand-600 sm:h-10 sm:w-10" />
              </span>
              <span
                className="absolute bottom-5 left-5 inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold"
                style={{ backgroundColor: "rgba(255,255,255,0.92)", color: "#101828" }}
              >
                <Clock className="h-3 w-3" />
                60 Sekunden Intro
              </span>
              {video.kind === "youtube" && (
                <span
                  className="absolute bottom-5 right-5 inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium"
                  style={{ backgroundColor: "rgba(255,255,255,0.92)", color: "#344054" }}
                >
                  <ShieldCheck className="h-3 w-3 text-brand-600" />
                  Lädt erst nach Klick von YouTube
                </span>
              )}
            </button>
          )}
        </m.div>

        {/* Drei Stichpunkte unter dem Video – so stand es schon im ersten Block. */}
        <div className="mx-auto mt-10 grid max-w-4xl grid-cols-1 gap-4 sm:grid-cols-3">
          {bullets.map((p) => (
            <div
              key={p.label}
              className="rounded-xl border p-4"
              style={{ borderColor: "var(--border)", backgroundColor: "var(--card-bg)" }}
            >
              <p className="text-sm font-semibold" style={{ color: "var(--text)" }}>
                {p.label}
              </p>
              <p className="mt-1 text-sm" style={{ color: "var(--text-tertiary)" }}>
                {p.sub}
              </p>
            </div>
          ))}
        </div>

        {/* Transkript (WCAG 1.2.3) – erscheint, sobald im Admin ein Text hinterlegt ist. */}
        {video.transcript && (
          <details
            id="video-transkript"
            className="mx-auto mt-6 max-w-3xl rounded-xl border px-5 py-4"
            style={{ borderColor: "var(--border)", backgroundColor: "var(--card-bg)" }}
          >
            <summary className="cursor-pointer text-sm font-semibold" style={{ color: "var(--text)" }}>
              Transkript des Videos anzeigen
            </summary>
            <div className="mt-3 space-y-3 text-sm leading-relaxed" style={{ color: "var(--text-secondary)" }}>
              {video.transcript.split(/\n{2,}/).map((p, i) => (
                <p key={i}>{p}</p>
              ))}
            </div>
          </details>
        )}
      </div>
    </section>
  );
}
